import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../constants';
import { Breadcrumb } from '../common';
import { formatUserDisplayName, getUserInitials } from '../../utils/authUtils';

const Header = ({ onMenuToggle, isMenuOpen = false }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // Get auth context
  const { user, logout, isLoggingOut } = useAuth();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = async () => {
    setIsUserMenuOpen(false);

    // Use AuthContext logout function instead of manual localStorage handling
    const result = await logout();

    if (result.success) {
      // Navigate to login page after successful logout
      navigate(ROUTES.LOGIN);
    }
  };

  return (
    <header className='bg-white shadow-sm border-b border-gray-200'>
      <div className='flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8'>
        {/* Left section: Hamburger menu (mobile) and Breadcrumbs (desktop) */}
        <div className='flex items-center'>
          {/* Hamburger menu for mobile */}
          <button
            type='button'
            className='inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden'
            onClick={onMenuToggle}
            aria-expanded='false'
          >
            <span className='sr-only'>Open main menu</span>
            {/* Hamburger icon */}
            {!isMenuOpen ? (
              <svg
                className='block h-6 w-6'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 6h16M4 12h16M4 18h16'
                />
              </svg>
            ) : (
              <svg
                className='block h-6 w-6'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            )}
          </button>

          {/* Breadcrumbs for desktop */}
          <div className='hidden lg:block ml-4'>
            <Breadcrumb />
          </div>
        </div>

        {/* Right section: User menu */}
        <div className='flex items-center'>
          {/* User menu dropdown */}
          <div className='relative' ref={userMenuRef}>
            <button
              type='button'
              className='flex items-center max-w-xs bg-white text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              onClick={handleUserMenuToggle}
              aria-expanded='false'
              aria-haspopup='true'
            >
              <span className='sr-only'>Open user menu</span>
              {user?.avatar ? (
                <img
                  className='w-8 h-8 rounded-full object-cover'
                  src={user.avatar}
                  alt={formatUserDisplayName(user)}
                />
              ) : (
                <div className='w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center'>
                  <span className='text-white text-sm font-medium'>
                    {getUserInitials(user)}
                  </span>
                </div>
              )}
              <span className='ml-2 text-sm font-medium text-gray-700 hidden sm:block'>
                {formatUserDisplayName(user)}
              </span>
              <svg
                className='ml-1 h-4 w-4 text-gray-400'
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 20 20'
                fill='currentColor'
                aria-hidden='true'
              >
                <path
                  fillRule='evenodd'
                  d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                  clipRule='evenodd'
                />
              </svg>
            </button>

            {/* User dropdown menu */}
            {isUserMenuOpen && (
              <div className='origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50'>
                <div className='py-1' role='menu' aria-orientation='vertical'>
                  {/* User Info Section */}
                  <div className='px-4 py-3 border-b border-gray-100'>
                    <p className='text-sm font-medium text-gray-900'>
                      {formatUserDisplayName(user)}
                    </p>
                    <p className='text-sm text-gray-500'>{user?.email}</p>
                    {user?.phone && (
                      <p className='text-xs text-gray-400'>{user.phone}</p>
                    )}
                    {user?.roles && (
                      <div className='mt-1'>
                        {Object.keys(user.roles).map(roleKey => (
                          <span
                            key={roleKey}
                            className='inline-block px-2 py-1 mt-1 mr-1 text-xs bg-blue-100 text-blue-800 rounded-full'
                          >
                            {roleKey}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Navigation Links */}
                  {/* <Link
                    to={ROUTES.PROFILE}
                    className='flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100'
                    role='menuitem'
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <svg
                      className='mr-3 h-4 w-4 text-gray-400'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                      />
                    </svg>
                    Your Profile
                  </Link> */}
                  <div className='border-t border-gray-100'></div>
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isLoggingOut ? 'bg-gray-50' : ''
                    }`}
                    role='menuitem'
                  >
                    {isLoggingOut ? (
                      <>
                        <div className='mr-3 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600'></div>
                        Signing out...
                      </>
                    ) : (
                      <>
                        <svg
                          className='mr-3 h-4 w-4 text-gray-400'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
                          />
                        </svg>
                        Sign out
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
